import { z } from 'zod'

/**
 * OAuth provider types supported by the application
 */
export type OAuthProvider = 'azure_ad' | 'auth0' | 'okta'

/**
 * Login request schema with Zod validation
 */
export const loginRequestSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(1, 'Password is required'),
  rememberMe: z.boolean().default(false),
})

export type LoginRequest = z.infer<typeof loginRequestSchema>

/**
 * Login form input type (before transform/defaults applied)
 */
export type LoginFormInput = z.input<typeof loginRequestSchema>

/**
 * Authentication tokens structure
 */
export interface AuthTokens {
  accessToken: string
  refreshToken: string
  expiresAt: number // Unix timestamp in milliseconds
}

/**
 * Linked OAuth account information
 */
export interface LinkedAccount {
  provider: OAuthProvider
  providerUserId: string
  email: string
  linkedAt: string
}

/**
 * Current authenticated user
 */
export interface AuthUser {
  id: string
  email: string
  displayName: string
  avatarUrl?: string
  tenantId: string
  roles: string[]
  linkedAccounts: LinkedAccount[]
  mustChangePassword?: boolean
  status?: string
}

/**
 * Login response from the API
 */
export interface LoginResponse {
  user: AuthUser
  tokens: AuthTokens
}

/**
 * OAuth redirect response
 */
export interface OAuthRedirectResponse {
  redirectUrl: string
  state: string
}

/**
 * OAuth callback request
 */
export interface OAuthCallbackRequest {
  code: string
  state: string
  provider: OAuthProvider
}

/**
 * Token refresh request
 */
export interface TokenRefreshRequest {
  refreshToken: string
}

/**
 * Token refresh response
 */
export interface TokenRefreshResponse {
  accessToken: string
  refreshToken: string
  expiresAt: number
}
